import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Share2, Link2, Linkedin, Twitter, Mail, Check, Copy } from "lucide-react";
import { useScenarioSharing } from "@/hooks/useScenarioSharing";

interface SharePanelProps {
  totalImpact: number;
  paybackMonths: number;
  language: string;
  formatCurrency: (value: number) => string;
  currentInputs?: {
    callsPerMonth: number;
    numberOfProperties: number;
    missedCallPercent: number;
    salesCallPercent: number;
    avgOrderValue: number;
    staffHoursWeek: number;
    hourlyStaffCost: number;
  };
}

const SharePanel = ({ totalImpact, paybackMonths, language, formatCurrency, currentInputs }: SharePanelProps) => {
  const [copied, setCopied] = useState(false);
  const { generateShareUrl, generateShareText } = useScenarioSharing();
  
  // Generate shareable URL with scenario data if inputs provided
  const shareUrl = currentInputs 
    ? generateShareUrl(currentInputs, language)
    : `${window.location.origin}/${language}#roi-calculator`;
  
  const shareText = currentInputs
    ? generateShareText(currentInputs, totalImpact, paybackMonths, language, formatCurrency)
    : (language === 'sv'
      ? `Jag beräknade mina besparingar med HostCall.ai: ${formatCurrency(totalImpact)}/år och återbetalningstid på ${paybackMonths.toFixed(1)} månader! 🚀`
      : `I calculated my savings with HostCall.ai: ${formatCurrency(totalImpact)}/year with a ${paybackMonths.toFixed(1)} month payback! 🚀`);
  
  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
  const emailSubject = language === 'sv' 
    ? 'Kolla in denna ROI-kalkylator' 
    : 'Check out this ROI calculator';
  const emailBody = `${shareText}\n\n${language === 'sv' ? 'Pröva själv' : 'Try it yourself'}: ${shareUrl}`;
  const emailUrl = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
  
  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Card className="p-4 bg-muted/50 border-border/50">
      <div className="flex items-center gap-2 mb-4">
        <Share2 className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium text-sm">
          {language === 'sv' ? 'Dela med en kollega' : 'Share with a colleague'}
        </span>
        {currentInputs && (
          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
            {language === 'sv' ? 'Inkl. dina siffror' : 'Incl. your numbers'}
          </span>
        )}
      </div>
      
      <div className="flex gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          className="flex-1 min-w-[120px]"
          onClick={copyToClipboard}
        >
          {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
          {copied 
            ? (language === 'sv' ? 'Kopierad!' : 'Copied!') 
            : (language === 'sv' ? 'Kopiera länk' : 'Copy Link')
          }
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          asChild
        >
          <a href={linkedInUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
            <Linkedin className="h-4 w-4" />
            <span className="sr-only sm:not-sr-only">LinkedIn</span>
          </a>
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          asChild
        >
          <a href={twitterUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
            <Twitter className="h-4 w-4" />
            <span className="sr-only sm:not-sr-only">X</span>
          </a>
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          asChild
        >
          <a href={emailUrl} className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            <span className="sr-only sm:not-sr-only">Email</span>
          </a>
        </Button>
      </div>
    </Card>
  );
};

export default SharePanel;
